import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Loader2, Plus, Pencil, Trash2, Eye, EyeOff, ImageIcon, Upload
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { useAuth } from "@/hooks/useAuth";
import { useAdmin } from "@/hooks/useAdmin";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  content: string;
  cover_image_url: string | null;
  author_id: string;
  status: string;
  published_at: string | null;
  created_at: string;
  updated_at: string;
}

const TEMPLATES = [
  {
    name: "Behind the Song",
    title: "Behind the Song: [Song Title]",
    content: `## The Story Behind This Song

Every custom song begins with a story — and this one was truly special.

### The Request

[Describe the occasion and what the customer wanted]

### The Creative Process

[Share insights about how the song was crafted]

### The Big Moment

[Describe the reaction or how the song was presented]

---

*Want a custom song for your special moment? [Get started today](/create).*`,
    excerpt: "Discover the heartfelt story behind one of our custom compositions.",
  },
  {
    name: "Gift Guide",
    title: "The Ultimate Guide to [Occasion] Gifts",
    content: `## Why a Custom Song Makes the Perfect Gift

Finding the right gift can be stressful. Here's why a personalized song stands out.

### 1. It's Truly One-of-a-Kind

Unlike store-bought gifts, a custom song is created specifically for your loved one.

### 2. It Captures Your Story

We weave your shared memories, inside jokes, and feelings into every lyric.

### 3. It Lasts Forever

A song can be replayed for years, bringing back those special emotions every time.

### How It Works

1. **Tell us your story** — Share your memories and what makes your relationship special
2. **Choose your style** — Pick from acoustic, pop, classical, and more
3. **We compose** — Our team creates a professional, studio-quality song
4. **You celebrate** — Present your gift and watch the magic happen

---

*Ready to create an unforgettable gift? [Order your song now](/create).*`,
    excerpt: "Discover why a custom song is the most meaningful gift you can give.",
  },
  {
    name: "Tips & Advice",
    title: "[Number] Tips for [Topic]",
    content: `## Introduction

[Brief intro about the topic and why it matters]

### Tip 1: [Title]

[Explanation]

### Tip 2: [Title]

[Explanation]

### Tip 3: [Title]

[Explanation]

## Final Thoughts

[Wrap up with a call to action]

---

*Looking for a truly personal touch? [Create a custom song](/create) for your next celebration.*`,
    excerpt: "Helpful tips and advice for making your celebration unforgettable.",
  },
  {
    name: "Blank Post",
    title: "",
    content: "",
    excerpt: "",
  },
];

const generateSlug = (title: string) =>
  title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

const AdminBlog = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { isAdmin, loading: adminLoading } = useAdmin();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);

  // Editor state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPost, setEditingPost] = useState<BlogPost | null>(null);
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [excerpt, setExcerpt] = useState("");
  const [content, setContent] = useState("");
  const [coverImageUrl, setCoverImageUrl] = useState("");
  const [postStatus, setPostStatus] = useState<string>("draft");
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    if (!authLoading && !adminLoading) {
      if (!user) navigate("/login");
      else if (!isAdmin) navigate("/dashboard");
    }
  }, [user, isAdmin, authLoading, adminLoading, navigate]);

  const fetchPosts = async () => {
    if (!user || !isAdmin) return;
    const { data, error } = await supabase
      .from("blog_posts")
      .select("*")
      .order("created_at", { ascending: false });
    if (!error && data) setPosts(data as BlogPost[]);
    setLoading(false);
  };

  useEffect(() => {
    if (isAdmin) fetchPosts();
  }, [isAdmin]);

  const resetForm = () => {
    setEditingPost(null);
    setTitle("");
    setSlug("");
    setExcerpt("");
    setContent("");
    setCoverImageUrl("");
    setPostStatus("draft");
  };

  const applyTemplate = (template: (typeof TEMPLATES)[number]) => {
    setTitle(template.title);
    setSlug(generateSlug(template.title));
    setContent(template.content);
    setExcerpt(template.excerpt);
  };

  const openEditor = (post?: BlogPost) => {
    if (post) {
      setEditingPost(post);
      setTitle(post.title);
      setSlug(post.slug);
      setExcerpt(post.excerpt || "");
      setContent(post.content);
      setCoverImageUrl(post.cover_image_url || "");
      setPostStatus(post.status);
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleImageUpload = async () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/jpeg,image/png,image/webp";
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      if (file.size > 5 * 1024 * 1024) {
        toast({ title: "File too large", description: "Max 5MB", variant: "destructive" });
        return;
      }
      setUploadingImage(true);
      const path = `covers/${Date.now()}-${file.name}`;
      const { error } = await supabase.storage.from("blog-images").upload(path, file);
      if (error) {
        toast({ title: "Upload failed", description: error.message, variant: "destructive" });
      } else {
        const { data: urlData } = supabase.storage.from("blog-images").getPublicUrl(path);
        setCoverImageUrl(urlData.publicUrl);
        toast({ title: "Image uploaded" });
      }
      setUploadingImage(false);
    };
    input.click();
  };

  const handleInsertImage = async () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/jpeg,image/png,image/webp";
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      if (file.size > 5 * 1024 * 1024) {
        toast({ title: "File too large", description: "Max 5MB", variant: "destructive" });
        return;
      }
      setUploadingImage(true);
      const path = `inline/${Date.now()}-${file.name}`;
      const { error } = await supabase.storage.from("blog-images").upload(path, file);
      if (error) {
        toast({ title: "Upload failed", description: error.message, variant: "destructive" });
      } else {
        const { data: urlData } = supabase.storage.from("blog-images").getPublicUrl(path);
        setContent((prev) => prev + `\n\n![${file.name}](${urlData.publicUrl})\n`);
        toast({ title: "Image inserted into content" });
      }
      setUploadingImage(false);
    };
    input.click();
  };

  const handleSave = async () => {
    if (!title.trim() || !content.trim()) {
      toast({ title: "Missing fields", description: "Title and content are required.", variant: "destructive" });
      return;
    }
    if (!user) return;
    setSaving(true);
    const postSlug = slug.trim() || generateSlug(title);
    const postData = {
      title: title.trim(),
      slug: postSlug,
      excerpt: excerpt.trim() || null,
      content: content.trim(),
      cover_image_url: coverImageUrl || null,
      status: postStatus,
      published_at: postStatus === "published" ? new Date().toISOString() : null,
      author_id: user.id,
    };

    if (editingPost) {
      const { error } = await supabase
        .from("blog_posts")
        .update(postData)
        .eq("id", editingPost.id);
      if (error) {
        toast({ title: "Error", description: error.message, variant: "destructive" });
      } else {
        toast({ title: "Post updated" });
        setDialogOpen(false);
        fetchPosts();
      }
    } else {
      const { error } = await supabase.from("blog_posts").insert(postData);
      if (error) {
        toast({ title: "Error", description: error.message, variant: "destructive" });
      } else {
        toast({ title: "Post created" });
        setDialogOpen(false);
        fetchPosts();
      }
    }
    setSaving(false);
  };

  const handleDelete = async (id: string) => {
    const { error } = await supabase.from("blog_posts").delete().eq("id", id);
    if (!error) {
      setPosts((prev) => prev.filter((p) => p.id !== id));
      toast({ title: "Post deleted" });
    }
  };

  const toggleStatus = async (post: BlogPost) => {
    const newStatus = post.status === "published" ? "draft" : "published";
    const { error } = await supabase
      .from("blog_posts")
      .update({
        status: newStatus,
        published_at: newStatus === "published" ? new Date().toISOString() : null,
      })
      .eq("id", post.id);
    if (!error) {
      setPosts((prev) =>
        prev.map((p) =>
          p.id === post.id
            ? { ...p, status: newStatus, published_at: newStatus === "published" ? new Date().toISOString() : null }
            : p
        )
      );
      toast({ title: newStatus === "published" ? "Post published" : "Post unpublished" });
    }
  };

  if (authLoading || adminLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <Button variant="ghost" size="sm" className="mb-4" onClick={() => navigate("/admin")}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>

          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-2">Blog Posts</h1>
              <p className="text-muted-foreground">Create and manage blog content.</p>
            </div>
            <Button onClick={() => openEditor()}>
              <Plus className="w-4 h-4 mr-2" /> New Post
            </Button>
          </div>

          {/* Post List */}
          {posts.length === 0 ? (
            <div className="bg-card rounded-2xl shadow-soft p-12 text-center">
              <p className="text-muted-foreground mb-4">No blog posts yet.</p>
              <Button onClick={() => openEditor()}>
                <Plus className="w-4 h-4 mr-2" /> Create Your First Post
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {posts.map((post) => (
                <div
                  key={post.id}
                  className="bg-card rounded-xl shadow-soft p-5 flex items-center justify-between gap-4"
                >
                  <div className="flex items-center gap-4 min-w-0">
                    {post.cover_image_url ? (
                      <img
                        src={post.cover_image_url}
                        alt=""
                        className="w-14 h-14 rounded-lg object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-14 h-14 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                        <ImageIcon className="w-6 h-6 text-muted-foreground" />
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="font-semibold text-foreground truncate">{post.title}</p>
                      <p className="text-sm text-muted-foreground">
                        <span
                          className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border ${
                            post.status === "published"
                              ? "bg-primary/10 text-primary border-primary/30"
                              : "bg-muted text-muted-foreground border-border"
                          }`}
                        >
                          {post.status === "published" ? (
                            <Eye className="w-3 h-3" />
                          ) : (
                            <EyeOff className="w-3 h-3" />
                          )}
                          {post.status}
                        </span>
                        <span className="ml-2">
                          {new Date(post.created_at).toLocaleDateString()}
                        </span>
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button variant="ghost" size="icon" onClick={() => toggleStatus(post)} title={post.status === "published" ? "Unpublish" : "Publish"}>
                      {post.status === "published" ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => openEditor(post)}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(post.id)}>
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Editor Dialog */}
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingPost ? "Edit Post" : "New Blog Post"}</DialogTitle>
              </DialogHeader>

              {/* Templates (only for new posts) */}
              {!editingPost && (
                <div className="mb-4">
                  <Label className="text-xs uppercase text-muted-foreground tracking-wide">Start from a template</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {TEMPLATES.map((t) => (
                      <Button key={t.name} variant="outline" size="sm" onClick={() => applyTemplate(t)}>
                        {t.name}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <Label htmlFor="blog-title">Title</Label>
                  <Input
                    id="blog-title"
                    value={title}
                    onChange={(e) => {
                      setTitle(e.target.value);
                      if (!editingPost) setSlug(generateSlug(e.target.value));
                    }}
                    placeholder="Post title"
                  />
                </div>
                <div>
                  <Label htmlFor="blog-slug">Slug</Label>
                  <Input id="blog-slug" value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="url-friendly-slug" />
                </div>
                <div>
                  <Label htmlFor="blog-excerpt">Excerpt</Label>
                  <Input id="blog-excerpt" value={excerpt} onChange={(e) => setExcerpt(e.target.value)} placeholder="Brief summary for the blog listing" />
                </div>
                <div>
                  <Label>Cover Image</Label>
                  <div className="flex items-center gap-3">
                    {coverImageUrl && (
                      <img src={coverImageUrl} alt="" className="w-20 h-14 rounded-lg object-cover" />
                    )}
                    <Button variant="outline" size="sm" onClick={handleImageUpload} disabled={uploadingImage}>
                      {uploadingImage ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                      {coverImageUrl ? "Change" : "Upload"}
                    </Button>
                    {coverImageUrl && (
                      <Button variant="ghost" size="sm" onClick={() => setCoverImageUrl("")}>Remove</Button>
                    )}
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label htmlFor="blog-content">Content (Markdown)</Label>
                    <Button variant="ghost" size="sm" onClick={handleInsertImage} disabled={uploadingImage}>
                      <ImageIcon className="w-4 h-4 mr-1" /> Insert Image
                    </Button>
                  </div>
                  <Textarea
                    id="blog-content"
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    placeholder="Write your blog post in Markdown..."
                    className="min-h-[250px] font-mono text-sm"
                  />
                </div>
                <div>
                  <Label htmlFor="blog-status">Status</Label>
                  <Select value={postStatus} onValueChange={setPostStatus}>
                    <SelectTrigger id="blog-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="published">Published</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-3 pt-2">
                  <Button onClick={handleSave} disabled={saving}>
                    {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                    {editingPost ? "Update Post" : "Create Post"}
                  </Button>
                  <Button variant="ghost" onClick={() => setDialogOpen(false)}>Cancel</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AdminBlog;
